import React, { useState } from "react";
import {
  Form,
  Button,
  Spinner,
  Container,
  Row,
  Col,
  Card,
  Modal,
} from "react-bootstrap";
import axios from "axios";
import "./recherches.css";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import Env from "../../hooks/Env";
import Input, { InputOption } from "../../components/Input";

const API_URL = Env().API_URL;

const EQUIPES = [
  { code: "GEER",  label: "Équipe 1 : Geosciences, Eau, Environnement et Ressources (GEER)" },
  { code: "IEMP",   label: "Équipe 2 : Ingénierie, Énergies, Matériaux et Procédés (IEMP)" },
  { code: "AGRISA", label: "Équipe 3 : Agriculture Durable & Souveraineté Alimentaire (AgriSA)" },
  { code: "TNLCM",  label: "Équipe 4 : Technologies Numériques, Langage, Communication et Marketing (TNLCM)" },
  { code: "DCMSN",  label: "Équipe 5 : Sciences des données, Calcul scientifique, Modélisation et Simulations Numériques (DCMSN)" },
  { code: "TIDG",   label: "Équipe 6 : Territoires, Innovation, Développement Durable et Gouvernance (TIDG)" },
];

const ETABLISSEMENTS = [
  { code: "POLYTECH_DIAMNIADIO", label: "Polytech Diamniadio" },
  { code: "UFR_STA",             label: "UFR STA" },
  { code: "UFR_TECNA",           label: "UFR TECNA" },
  { code: "UFR_SEG",             label: "UFR SEG" },
];

const EMPTY_FORM = {
  prenom: "",
  nom: "",
  email: "",
  etablissement: "",
  premier_choix: "",
  deuxieme_choix: "",
  troisieme_choix: "",
};

const Recherches = () => {
  useDocumentTitle("Inscription EPR - UAM");

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState(EMPTY_FORM);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      // reset dependent choices if premier_choix changes
      if (name === "premier_choix") {
        updated.deuxieme_choix = "";
        updated.troisieme_choix = "";
      }
      if (name === "deuxieme_choix") {
        updated.troisieme_choix = "";
      }
      return updated;
    });
  };

  const getAvailableForChoice = (excludeCodes) =>
    EQUIPES.filter((e) => !excludeCodes.includes(e.code));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const payload = {
      prenom: formData.prenom.trim(),
      nom: formData.nom.trim(),
      email: formData.email.trim().toLowerCase(),
      etablissement: formData.etablissement,
      premier_choix: formData.premier_choix,
      deuxieme_choix: formData.deuxieme_choix || null,
      troisieme_choix: formData.troisieme_choix || null,
    };

    try {
      const response = await axios.post(`${API_URL}/recherches/`, payload, {
        headers: { "Content-Type": "application/json" },
      });
      setSuccessData(response.data?.data || null);
      setSuccess(true);
      setFormData(EMPTY_FORM);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Une erreur est survenue lors de la soumission."
      );
    } finally {
      setLoading(false);
    }
  };

  const choix2Options = getAvailableForChoice(
    formData.premier_choix ? [formData.premier_choix] : []
  );
  const choix3Options = getAvailableForChoice(
    [formData.premier_choix, formData.deuxieme_choix].filter(Boolean)
  );

  return (
    <Container className="recherches-container py-5">
      <Row className="justify-content-center">
        <Col md={9} lg={7}>
          <Card className="recherches-card shadow">
            <Card.Header className="recherches-header text-center">
              <h3 className="mb-1">
                Constitution des Équipes Pluridisciplinaires de Recherche (EPR)
              </h3>
              <p className="mb-0">Université Amadou Mahtar MBOW — Formulaire d'inscription</p>
            </Card.Header>

            <Card.Body className="p-4">
              {/* Modal succès */}
              <Modal show={success} onHide={() => setSuccess(false)} centered className="recherches-modal-success">
                <Modal.Header closeButton>
                  <Modal.Title>Inscription enregistrée</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="70"
                    height="70"
                    fill="#00A7E1"
                    viewBox="0 0 16 16"
                    className="mb-3"
                  >
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                  </svg>
                  <h5 className="mb-2">Votre inscription a été soumise avec succès !</h5>
                  {successData && (
                    <p className="text-muted mb-0" style={{ fontSize: "0.92rem" }}>
                      <strong>{successData.prenom} {successData.nom}</strong><br />
                      Premier choix : <strong>{successData.premier_choix}</strong>
                      {successData.deuxieme_choix && (
                        <> — Deuxième choix : <strong>{successData.deuxieme_choix}</strong></>
                      )}
                    </p>
                  )}
                  <p className="text-muted mt-2" style={{ fontSize: "0.85rem" }}>
                    Un email de confirmation vous a été envoyé.
                  </p>
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                  <Button onClick={() => setSuccess(false)}>
                    Fermer
                  </Button>
                </Modal.Footer>
              </Modal>

              {/* Modal erreur */}
              <Modal show={!!error} onHide={() => setError("")} centered>
                <Modal.Header closeButton className="bg-danger text-white">
                  <Modal.Title>Erreur</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="70"
                    height="70"
                    fill="#dc3545"
                    viewBox="0 0 16 16"
                    className="mb-3"
                  >
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
                  </svg>
                  <h5 className="mb-2">Une erreur est survenue</h5>
                  <p className="text-muted mb-0">{error}</p>
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                  <Button variant="danger" onClick={() => setError("")}>
                    Fermer
                  </Button>
                </Modal.Footer>
              </Modal>

              {/* Consigne */}
              <div className="recherches-consigne">
                <p><strong>Consigne :</strong></p>
                <ul>
                  <li>
                    Chaque enseignant-chercheur peut choisir d'être dans{" "}
                    <strong>3 équipes au maximum</strong>.
                  </li>
                  <li>
                    L'enseignant-chercheur est <strong>titulaire</strong> dans l'équipe
                    de son <strong>premier choix</strong> et sera considéré comme{" "}
                    <strong>membre associé</strong> ou{" "}
                    <strong>« personne ressource »</strong> dans les autres équipes
                    (choix 2 et/ou 3).
                  </li>
                </ul>
              </div>

              <Form onSubmit={handleSubmit}>
                {/* ── Informations personnelles ── */}
                <p className="recherches-section-title">Informations personnelles</p>

                <Row>
                  <Col md={6}>
                    <Input
                      className="mb-3"
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      label="Prénom"
                      placeholder="Votre prénom"
                    />
                  </Col>
                  <Col md={6}>
                    <Input
                      className="mb-3"
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      label="Nom"
                      placeholder="Votre nom"
                    />
                  </Col>
                </Row>

                <Input
                  className="mb-3"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  label="Adresse email"
                  placeholder="prenom.nom@uam.sn"
                />

                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">
                    Établissement <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="etablissement"
                    value={formData.etablissement}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Sélectionnez --</option>
                    {ETABLISSEMENTS.map((e) => (
                      <InputOption key={e.code} value={e.code} label={e.label} />
                    ))}
                  </Form.Select>
                </Form.Group>

                {/* ── Choix des équipes ── */}
                <p className="recherches-section-title mt-3">Choix des équipes de recherche</p>

                {/* Premier choix */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">
                    Premier choix{" "}
                    <span className="text-danger">*</span>{" "}
                    <span className="choix-badge titulaire">Titulaire</span>
                  </Form.Label>
                  <Form.Select
                    name="premier_choix"
                    value={formData.premier_choix}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Sélectionnez votre équipe principale --</option>
                    {EQUIPES.map((e) => (
                      <InputOption key={e.code} value={e.code} label={e.label} />
                    ))}
                  </Form.Select>
                </Form.Group>

                {/* Deuxième choix */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">
                    Deuxième choix{" "}
                    <span className="text-muted" style={{ fontSize: "0.85rem" }}>(optionnel)</span>{" "}
                    {/* <span className="choix-badge associe">Membre associé</span> */}
                  </Form.Label>
                  <Form.Select
                    name="deuxieme_choix"
                    value={formData.deuxieme_choix}
                    onChange={handleChange}
                    disabled={!formData.premier_choix}
                  >
                    <option value="">-- Aucun (optionnel) --</option>
                    {choix2Options.map((e) => (
                      <InputOption key={e.code} value={e.code} label={e.label} />
                    ))}
                  </Form.Select>
                  {!formData.premier_choix && (
                    <Form.Text className="text-muted">
                      Sélectionnez d'abord votre premier choix.
                    </Form.Text>
                  )}
                </Form.Group>

                {/* Troisième choix */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-medium">
                    Troisième choix{" "}
                    <span className="text-muted" style={{ fontSize: "0.85rem" }}>(optionnel)</span>{" "}
                    {/* <span className="choix-badge associe">Personne ressource</span> */}
                  </Form.Label>
                  <Form.Select
                    name="troisieme_choix"
                    value={formData.troisieme_choix}
                    onChange={handleChange}
                    disabled={!formData.deuxieme_choix}
                  >
                    <option value="">-- Aucun (optionnel) --</option>
                    {choix3Options.map((e) => (
                      <InputOption key={e.code} value={e.code} label={e.label} />
                    ))}
                  </Form.Select>
                  {!formData.deuxieme_choix && (
                    <Form.Text className="text-muted">
                      Sélectionnez d'abord un deuxième choix.
                    </Form.Text>
                  )}
                </Form.Group>

                {/* Bouton soumettre */}
                <div className="d-grid">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={loading}
                    className="recherches-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Envoi en cours...
                      </>
                    ) : (
                      "Soumettre mon inscription"
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Recherches;
